import { Instant, LocalDate, ZoneId, ZonedDateTime } from "@js-joda/core";
import { ExportToCsv } from "export-to-csv";
import { TFunction } from "i18next";
import { TimeReportResolution, TimeReportStatusV2 } from "model/TimeReport";
import {
  TimeReportsV2Group,
  TimeReportsV2Item,
} from "services/api/company/timeReports.service";
import {
  parseIsoMillis,
} from "web-apps/company/utils/timeAndDate";

const STOCKHOLM_ZONE_ID = ZoneId.of("Europe/Stockholm");

const resolveMonthIso = (monthDate: LocalDate): string => {
  return `${String(monthDate.year())}-${String(monthDate.monthValue()).padStart(2, "0")}`;
};

const resolveEffectiveRange = (
  item: TimeReportsV2Item,
): { start: string; end: string } | null => {
  if (item.stampedStartTime && item.stampedEndTime) {
    return { start: item.stampedStartTime, end: item.stampedEndTime };
  }
  if (item.reportedStartTime && item.reportedEndTime) {
    return { start: item.reportedStartTime, end: item.reportedEndTime };
  }
  if (item.plannedStartTime && item.plannedEndTime) {
    return { start: item.plannedStartTime, end: item.plannedEndTime };
  }
  return null;
};

const resolveSortMillis = (item: TimeReportsV2Item): number => {
  const range = resolveEffectiveRange(item);
  if (range) {
    const rangeMillis = parseIsoMillis(range.start);
    if (rangeMillis != null) return rangeMillis;
  }
  const updatedAtMillis = parseIsoMillis(item.updatedAt);
  if (updatedAtMillis != null) return updatedAtMillis;
  return Number.MAX_SAFE_INTEGER;
};

const resolveEffectiveRangeStrict = (
  item: TimeReportsV2Item,
): { start: string; end: string } => {
  const range = resolveEffectiveRange(item);
  if (!range) {
    throw new Error(
      `Missing effective time range for time report ${String(item.id)}`,
    );
  }
  return range;
};

const parseStockholmDateTimeStrict = (
  value: string,
  fieldName: string,
  itemId: number,
): ZonedDateTime => {
  try {
    return ZonedDateTime.ofInstant(Instant.parse(value), STOCKHOLM_ZONE_ID);
  } catch {
    throw new Error(
      `Invalid ISO value for ${fieldName} on time report ${String(itemId)}: ${value}`,
    );
  }
};

const minutesBetweenStrict = (
  startValue: string,
  endValue: string,
  fieldName: string,
  itemId: number,
  options?: { allowEqual?: boolean },
): number => {
  const startMillis = parseIsoMillis(startValue);
  const endMillis = parseIsoMillis(endValue);
  const allowsEqual = options?.allowEqual === true;

  if (startMillis == null || endMillis == null) {
    throw new Error(
      `Invalid ISO value for ${fieldName} on time report ${String(itemId)} (${startValue} -> ${endValue})`,
    );
  }
  if (endMillis < startMillis || (!allowsEqual && endMillis === startMillis)) {
    throw new Error(
      `Invalid time range for ${fieldName} on time report ${String(itemId)} (${startValue} -> ${endValue})`,
    );
  }

  return Math.floor((endMillis - startMillis) / 60000);
};

const formatMinutesPretty = (minutes: number, t: TFunction): string => {
  const safeMinutes = Math.max(0, Math.round(minutes));
  const hours = Math.floor(safeMinutes / 60);
  const rest = safeMinutes % 60;
  if (hours === 0 && rest === 0) {
    return `0${t("General.MinutesShortened")}`;
  }
  return `${hours ? `${hours}${t("General.HourShortened")}` : ""} ${
    rest ? `${rest}${t("General.MinutesShortened")}` : ""
  }`.trim();
};

const minutesToDecimal = (minutes: number): number =>
  parseFloat((minutes / 60).toFixed(2));

const toBreakMinutes = (item: TimeReportsV2Item): number => {
  if (!item.breakStartTime && !item.breakEndTime) return 0;
  if (!item.breakStartTime || !item.breakEndTime) {
    throw new Error(
      `Incomplete break range on time report ${String(item.id)} (${String(item.breakStartTime)} -> ${String(item.breakEndTime)})`,
    );
  }
  return minutesBetweenStrict(
    item.breakStartTime,
    item.breakEndTime,
    "break",
    item.id,
    { allowEqual: true },
  );
};

const toReportMinutes = (item: TimeReportsV2Item): number => {
  const range = resolveEffectiveRangeStrict(item);
  const grossMinutes = minutesBetweenStrict(
    range.start,
    range.end,
    "work",
    item.id,
  );
  const breakMinutes = toBreakMinutes(item);
  const netMinutes = grossMinutes - breakMinutes;
  if (netMinutes < 0) {
    throw new Error(
      `Negative net minutes for time report ${String(item.id)} (gross ${String(grossMinutes)}, break ${String(breakMinutes)})`,
    );
  }
  return netMinutes;
};

const toCsvStatus = (status: TimeReportStatusV2): string => {
  if (
    status === TimeReportStatusV2.NEW ||
    status === TimeReportStatusV2.UNDEFINED
  ) {
    return "Unsubmitted";
  }
  return status;
};

const isApprovedForTotals = (item: TimeReportsV2Item): boolean =>
  item.status === TimeReportStatusV2.APPROVED ||
  (item.status === TimeReportStatusV2.CLOSED &&
    (item.resolution === TimeReportResolution.TIME_REPORTED ||
      item.resolution === TimeReportResolution.PAID ||
      item.resolution === TimeReportResolution.PAYONDEMAND));

const normalizeWorkerName = (value: string, fallbackId: number): string => {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  if (normalized.length > 0) return normalized;
  return `worker-${String(fallbackId)}`;
};

const getTableOneOptionsSingleWorker = (fileName: string) => ({
  fieldSeparator: ";",
  quoteStrings: '"',
  decimalSeparator: ".",
  showLabels: true,
  showTitle: true,
  title: "TimeReports",
  filename: fileName,
  useTextFile: false,
  useBom: true,
  headers: [
    "role",
    "date",
    "shift",
    "break",
    "breakDec",
    "time",
    "timeDec",
    "status",
    "resolution",
    "totalCost",
  ],
});

const getTableTwoOptionsSingleWorker = (
  tableOneCsv: string,
  fileName: string,
) => ({
  fieldSeparator: ";",
  quoteStrings: '"',
  decimalSeparator: ".",
  showLabels: true,
  showTitle: true,
  title: tableOneCsv,
  filename: fileName,
  useTextFile: false,
  useBom: true,
  headers: [
    "Total cost",
    "Total salary",
    "Social Fees",
    "Total OB",
    "Simflexity Fee",
    "Total time",
    "Total Time Decimal",
    "Approved cost",
    "Approved time",
    "Approved time Decimal",
  ],
});

export const exportPersonRowToCsv = (
  row: TimeReportsV2Group,
  monthDate: LocalDate,
  t: TFunction,
): void => {
  const monthIso = resolveMonthIso(monthDate);
  const fileName = `time-reports-v2_${normalizeWorkerName(row.name, row.id)}_${monthIso}`;
  const sortedReports = [...row.items].sort((a, b) => {
    return resolveSortMillis(a) - resolveSortMillis(b);
  });

  const tableRows: Array<{
    role: string;
    date: string;
    shift: string;
    break: string;
    breakDec: number;
    time: string;
    timeDec: number;
    status: string;
    resolution: TimeReportResolution;
    totalCost: number;
  }> = [];
  const skippedRows: Array<{ timeReportId: number; reason: string }> = [];
  let totalMinutes = 0;
  let approvedMinutes = 0;

  for (const item of sortedReports) {
    try {
      const range = resolveEffectiveRangeStrict(item);
      const startTime = parseStockholmDateTimeStrict(
        range.start,
        "start",
        item.id,
      );
      const endTime = parseStockholmDateTimeStrict(range.end, "end", item.id);
      const weekDayString = `WeekDays.${startTime.dayOfWeek().name()}SHORT`;
      const breakMinutes = toBreakMinutes(item);
      const reportMinutes = toReportMinutes(item);
      const shiftStart = `${startTime.hour().toString().padStart(2, "0")}:${startTime
        .minute()
        .toString()
        .padStart(2, "0")}`;
      const shiftEnd = `${endTime.hour().toString().padStart(2, "0")}:${endTime
        .minute()
        .toString()
        .padStart(2, "0")}`;

      tableRows.push({
        role: row.role ?? "",
        date: `${t(weekDayString)} ${startTime.dayOfMonth()}/${startTime.monthValue()}`,
        shift: `${shiftStart} - ${shiftEnd}`,
        break: `${String(breakMinutes)} ${t("General.MinutesShortened")}`,
        breakDec: minutesToDecimal(breakMinutes),
        time: formatMinutesPretty(reportMinutes, t),
        timeDec: minutesToDecimal(reportMinutes),
        status: toCsvStatus(item.status),
        resolution: item.resolution,
        totalCost: 0,
      });
      totalMinutes += reportMinutes;
      if (isApprovedForTotals(item)) {
        approvedMinutes += reportMinutes;
      }
    } catch (error: unknown) {
      const reason =
        error instanceof Error ? error.message : "Unknown row export error";
      skippedRows.push({ timeReportId: item.id, reason });
    }
  }

  if (skippedRows.length > 0) {
    console.warn("[TimeReportsV2][DownloadCsv] Skipped invalid rows", {
      rowId: row.id,
      rowName: row.name,
      skippedCount: skippedRows.length,
      skippedRows,
    });
  }

  const totalsRow = {
    "Total cost": 0,
    "Total salary": 0,
    "Social Fees": 0,
    "Total OB": 0,
    "Simflexity Fee": 0,
    "Total time": formatMinutesPretty(totalMinutes, t),
    "Total Time Decimal": minutesToDecimal(totalMinutes),
    "Approved cost": 0,
    "Approved time": formatMinutesPretty(approvedMinutes, t),
    "Approved time Decimal": minutesToDecimal(approvedMinutes),
  };

  const tableOneOptions = getTableOneOptionsSingleWorker(fileName);
  const csvExporterOne = new ExportToCsv(tableOneOptions);
  const tableOneCsv = csvExporterOne.generateCsv(tableRows, true);
  const tableTwoOptions = getTableTwoOptionsSingleWorker(tableOneCsv, fileName);
  const csvExporterTwo = new ExportToCsv(tableTwoOptions);
  csvExporterTwo.generateCsv([totalsRow]);
};
